import type { ReactNode } from 'react'
import React, { useMemo, createContext, useContext } from 'react'
import { usePersonPageParams } from '../personPageRouteUtil'
import type { ResearchHelpData } from '../../data-service/useResearchHelpData'
import useResearchHelpData from '../../data-service/useResearchHelpData'
import { useFocusFamilyMembersContext } from './FocusFamilyMembersContext'

type ResearchHelpContextValue = {
  researchHelpData: ResearchHelpData
}

// Exported so component tests can provide research help data without fetching it
export const ResearchHelpContext = createContext<ResearchHelpContextValue | null>(null)

type Props = {
  children: ReactNode
  isResearcherGroup?: boolean
  treeId?: string
}

export default function ResearchHelpProvider({ children, isResearcherGroup, treeId }: Props): JSX.Element {
  const { focusFamilyMembers, loadingFamilyMembers } = useFocusFamilyMembersContext()
  const { pid } = usePersonPageParams('full')
  const researchHelpData = useResearchHelpData(
    pid,
    focusFamilyMembers.researchHelp,
    isResearcherGroup || loadingFamilyMembers,
    treeId
  )
  const value = useMemo(
    () => ({ researchHelpData, focusFamilyMembers, loadingFamilyMembers }),
    [researchHelpData, focusFamilyMembers, loadingFamilyMembers]
  )

  return <ResearchHelpContext.Provider value={value}>{children}</ResearchHelpContext.Provider>
}

export const useResearchHelpContext = (): ResearchHelpContextValue => {
  const context = useContext(ResearchHelpContext)
  if (!context) {
    throw new Error('useResearchHelpContext must be used within a ResearchHelpProvider')
  }
  return context
}
